import React, { useState, useMemo } from "react";
import Icon from "./Icon";
import "../../styles/ai/ai-sidebar.css";

function getDateLabel(ts) {
    if (!ts) return "Earlier";
    const date  = new Date(ts);
    const now   = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const d     = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diff  = Math.floor((today - d) / 86400000);

    if (diff === 0) return "Today";
    if (diff === 1) return "Yesterday";

    return date.toLocaleDateString("en-GB", {
        day:   "2-digit",
        month: "2-digit",
        year:  "numeric",
    });
}

export default function AiSidebar({
                                      chats = [],
                                      activeChatId = null,
                                      setActiveChatId = () => {},
                                      createNewChat = () => {},
                                      deleteChat = () => {},
                                      collapsed = false,
                                      user = null,
                                  }) {
    const [search, setSearch] = useState("");

    const filteredChats = useMemo(() => {
        const term = search.toLowerCase().trim();
        if (!term) return chats;
        return chats.filter((c) => c.title?.toLowerCase().includes(term));
    }, [chats, search]);

    const groupedChats = useMemo(() => {
        const groups = {};
        filteredChats.forEach((chat) => {
            const label = getDateLabel(chat.createdAt);
            if (!groups[label]) groups[label] = [];
            groups[label].push(chat);
        });
        return Object.entries(groups);
    }, [filteredChats]);

    if (collapsed) return null;

    return (
        <aside className="ai-sidebar">
            <div className="sidebar-header">
                <div className="sidebar-brand">
                    <div className="sidebar-brand-icon">
                        <Icon name="sparkle" size={16} />
                    </div>
                    <span className="sidebar-brand-text">OGM AI</span>
                </div>

                <button className="sidebar-new-chat" onClick={createNewChat}>
                    <Icon name="plus" size={15} />
                    New Chat
                </button>

                <div className="sidebar-search">
                    <Icon name="search" size={14} className="sidebar-search-icon" />
                    <input
                        type="text"
                        placeholder="Search chats…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            <div className="sidebar-chat-list">
                {filteredChats.length === 0 && (
                    <div className="sidebar-empty">
                        {search ? "No matches found" : "No conversations yet"}
                    </div>
                )}

                {groupedChats.map(([label, groupChats]) => (
                    <div key={label} className="sidebar-date-group">
                        <div className="sidebar-section-label">{label}</div>

                        {groupChats.map((chat) => {
                            const isActive = chat.id === activeChatId;
                            const title    = chat.title?.trim() || "Untitled Chat";

                            return (
                                <div
                                    key={chat.id}
                                    className={`sidebar-chat-item ${isActive ? "active" : ""}`}
                                    onClick={() => setActiveChatId(chat.id)}
                                >
                                    <Icon name="chat" size={14} className="sidebar-chat-icon" />
                                    <span className="sidebar-chat-title">{title}</span>

                                    <div className="sidebar-chat-actions">
                                        <button
                                            className="sidebar-action-btn danger"
                                            title="Delete chat"
                                            onClick={(e) => { e.stopPropagation(); deleteChat(chat.id); }}
                                            aria-label="Delete chat"
                                        >
                                            <Icon name="trash" size={13} />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>

            <div className="sidebar-footer">
                {user?.avatar ? (
                    <img
                        src={user.avatar}
                        alt={user.name}
                        className="sidebar-avatar sidebar-avatar-img"
                        referrerPolicy="no-referrer"
                    />
                ) : (
                    <div className="sidebar-avatar">{user?.initials || "U"}</div>
                )}
                <div className="sidebar-user-info">
                    <div className="sidebar-user-name">{user?.name || "User"}</div>
                    <div className="sidebar-user-role">{user?.role || "Member"}</div>
                </div>
            </div>
        </aside>
    );
}
